import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ChatProvider, PoolingProvider } from 'chat-interface-lib';
import { AuthProvider, useAuth } from './contexts/AuthProvider';
import { I18nProvider } from './contexts/I18nProvider';
import { AgentSidebar } from './components/AgentSidebar';
import { AgentTopBar } from './components/AgentTopBar';
import { HomeScreen } from './components/HomeScreen';
import { AgentChat } from './components/AgentChat';
import { EmbeddedContent } from './components/EmbeddedContent';
import { LoginPage } from './components/LoginPage';
import { SetupDashboard } from './components/SetupDashboard';
import { PublicAgentChat } from './components/PublicAgentChat';
import { Menu } from 'lucide-react';
import { useMultiAgentThemeSync } from './hooks/useMultiAgentThemeSync';
import { useTenantConfig } from './hooks/useTenantConfig';
import { applyBackendBranding } from './utils/applyBackendTheme';
import { Toaster } from './components/ui/toaster';


function AppContent() {
  // Initialize theme
  const [sidebarOpen, setSidebarOpen] = useState(true); // Start with sidebar open
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <Router>
      <AppContentInner 
        isMobile={isMobile} 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen} 
      />
    </Router>
  );
}

function AppContentInner({
  isMobile,
  sidebarOpen,
  setSidebarOpen
}: {
  isMobile: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}) {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Load tenant configuration
  const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
  const { tenantData } = useTenantConfig(tenantSlug);

  // Apply backend branding when tenant data is loaded
  React.useEffect(() => {
    if (tenantData) {
      applyBackendBranding(tenantData);
    }
  }, [tenantData]);

  // Sync theme changes from chat-interface-lib to multi-agent-chat DOM
  useMultiAgentThemeSync();
  
  // Auto-hide sidebar when navigating to an agent (mobile only)
  const location = useLocation();
  React.useEffect(() => {
    if (isMobile && location.pathname.startsWith('/agent/')) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, setSidebarOpen]);

  // Check if we're on a public route (bypasses authentication)
  const isPublicRoute = location.pathname.startsWith('/public/');

  // Show loading state while checking authentication (skip for public routes)
  if (!isPublicRoute && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show public route without authentication
  if (isPublicRoute) {
    return (
      <div className="bg-background text-foreground transition-colors duration-200 w-full multi-agent-chat-root" style={{ height: '100vh', maxHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route path="/public/:tenantSlug/:agentNameSlug" element={<PublicAgentChat />} />
        </Routes>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Authenticated content
  return (
    <div className="bg-background text-foreground transition-colors duration-200 min-h-screen w-full multi-agent-chat-root" style={{ height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>
        {isMobile ? (
          // Mobile layout with overlay sidebar
          <div className="relative h-full">
            {/* Overlay backdrop when sidebar is open */}
            {sidebarOpen && (
              <div 
                className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            
            {/* Sidebar overlay */}
            <div className={`fixed top-0 left-0 h-full z-50 transition-transform duration-300 ease-in-out ${
              sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
              <AgentSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
            </div>
            
            {/* Main content */}
            <div className="h-full bg-background">
              {/* Navigation Toggle Button - always visible on mobile */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="fixed top-4 left-4 z-30 p-2 bg-card hover:bg-muted rounded-lg shadow-lg border border-border transition-colors"
                title="Toggle Navigation"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </button>

              <Routes>
                <Route path="/" element={<HomeScreen />} />
                <Route path="/agent/:agentId" element={<AgentChat />} />
                <Route path="/embedded/:embeddedId" element={<EmbeddedContent />} />
                <Route path="/setup" element={user?.role === 'admin' ? <div className="h-full overflow-y-auto"><SetupDashboard /></div> : <Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        ) : (
          // Desktop layout with topbar
          <div className="flex flex-col h-screen">
            <AgentTopBar />

            <div className="flex-1 bg-background overflow-hidden">
              <Routes>
                <Route path="/" element={<HomeScreen />} />
                <Route path="/agent/:agentId" element={<AgentChat />} />
                <Route path="/embedded/:embeddedId" element={<EmbeddedContent />} />
                <Route path="/setup" element={user?.role === 'admin' ? <div className="h-full overflow-y-auto"><SetupDashboard /></div> : <Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        )}
        
      </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ChatProvider>
        <PoolingProvider>
          <I18nProvider>
            <AppContent />
            <Toaster />
          </I18nProvider>
        </PoolingProvider>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;